/**
 * Unified Logger Utility
 * 
 * Provides consistent logging across the application with:
 * - Production-aware logging (verbose in dev, minimal in prod)
 * - Structured log levels (debug, info, warn, error)
 * - Context prefixes for easy filtering
 * - Performance timing helpers
 * - Easy migration path to external logging services (Sentry, LogRocket, etc.)
 * 
 * @example
 * ```typescript
 * import { logger } from '@/lib/logger';
 * 
 * logger.info('User logged in', { userId: '123' });
 * logger.warn('Rate limit approaching', { remaining: 10 });
 * logger.error('Failed to fetch data', error);
 * logger.debug('Cache hit', { key: 'articles' }); // Only in development
 * ```
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  [key: string]: unknown;
}

interface LoggerConfig {
  /** Minimum log level to output (default: 'debug' in dev, 'warn' in prod) */
  minLevel?: LogLevel;
  /** Prefix for all log messages */
  prefix?: string;
  /** Enable/disable logging entirely */
  enabled?: boolean;
}

interface Logger {
  debug(message: string, context?: LogContext): void;
  info(message: string, context?: LogContext): void;
  warn(message: string, context?: LogContext): void;
  error(message: string, error?: unknown, context?: LogContext): void;
  child(prefix: string): Logger;
  time<T>(label: string, operation: () => Promise<T>): Promise<T>;
  group(label: string, fn: () => void): void;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

const isDevelopment = process.env.NODE_ENV === 'development';
const isTest = process.env.NODE_ENV === 'test';

// Default configuration
const defaultConfig: Required<LoggerConfig> = {
  minLevel: isDevelopment ? 'debug' : 'warn',
  prefix: '',
  enabled: !isTest, // Disable logs in test environment
};

/**
 * Format log context for output
 */
function formatContext(context?: LogContext): string {
  if (!context || Object.keys(context).length === 0) return '';
  
  try {
    // Truncate long values
    const truncated = Object.entries(context).reduce((acc, [key, value]) => {
      if (typeof value === 'string' && value.length > 200) {
        acc[key] = value.substring(0, 200) + '...';
      } else {
        acc[key] = value;
      }
      return acc;
    }, {} as LogContext);
    
    return ' ' + JSON.stringify(truncated);
  } catch {
    return ' [context serialization failed]';
  }
}

/**
 * Format error for logging
 */
function formatError(error: unknown): string {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}`;
  }
  if (typeof error === 'string') {
    return error;
  }
  return String(error);
}

/**
 * Create a logger instance with optional configuration
 */
function createLogger(config: LoggerConfig = {}): Logger {
  const cfg = { ...defaultConfig, ...config };
  
  const shouldLog = (level: LogLevel): boolean => {
    if (!cfg.enabled) return false;
    return LOG_LEVELS[level] >= LOG_LEVELS[cfg.minLevel];
  };
  
  const formatMessage = (level: LogLevel, message: string): string => {
    const timestamp = new Date().toISOString();
    const prefix = cfg.prefix ? `[${cfg.prefix}] ` : '';
    return `[${timestamp}] [${level.toUpperCase()}] ${prefix}${message}`;
  };
  
  return {
    /**
     * Debug level - Only shown in development
     * Use for: Detailed debugging info, cache hits/misses, flow tracing
     */
    debug(message: string, context?: LogContext): void {
      if (!shouldLog('debug')) return;
      console.debug(formatMessage('debug', message) + formatContext(context));
    },
    
    /**
     * Info level - General operational information
     * Use for: Service starts, successful operations, metrics
     */
    info(message: string, context?: LogContext): void {
      if (!shouldLog('info')) return;
      console.info(formatMessage('info', message) + formatContext(context));
    },
    
    /**
     * Warning level - Potentially problematic situations
     * Use for: Deprecations, retry attempts, fallback behavior
     */
    warn(message: string, context?: LogContext): void {
      if (!shouldLog('warn')) return;
      console.warn(formatMessage('warn', message) + formatContext(context));
    },
    
    /**
     * Error level - Error conditions
     * Use for: Caught exceptions, failed operations, service errors
     */
    error(message: string, error?: unknown, context?: LogContext): void {
      if (!shouldLog('error')) return;
      const errorStr = error ? ` - ${formatError(error)}` : '';
      console.error(formatMessage('error', message + errorStr) + formatContext(context));
      
      // In development, also log the full error stack
      if (isDevelopment && error instanceof Error && error.stack) {
        console.error(error.stack);
      }
    },
    
    /**
     * Create a child logger with a prefix
     * Useful for service-specific logging
     */
    child(prefix: string): Logger {
      const newPrefix = cfg.prefix ? `${cfg.prefix}:${prefix}` : prefix;
      return createLogger({ ...cfg, prefix: newPrefix });
    },
    
    /**
     * Time an async operation and log the duration
     */
    async time<T>(label: string, operation: () => Promise<T>): Promise<T> {
      const start = performance.now();
      try {
        const result = await operation();
        const duration = Math.round(performance.now() - start);
        this.debug(`${label} completed`, { durationMs: duration });
        return result;
      } catch (error) {
        const duration = Math.round(performance.now() - start);
        this.error(`${label} failed`, error, { durationMs: duration });
        throw error;
      }
    },
    
    /**
     * Group related logs together (only in development)
     */
    group(label: string, fn: () => void): void {
      if (!isDevelopment || !cfg.enabled) {
        fn();
        return;
      }
      console.group(label);
      try {
        fn();
      } finally {
        console.groupEnd();
      }
    },
  };
}

// Default logger instance
export const logger = createLogger();

// Service-specific loggers (pre-configured with context)
export const loggers = {
  auth: createLogger({ prefix: 'Auth' }),
  api: createLogger({ prefix: 'API' }),
  firebase: createLogger({ prefix: 'Firebase' }),
  twitter: createLogger({ prefix: 'Twitter' }),
  tags: createLogger({ prefix: 'Tags' }),
  content: createLogger({ prefix: 'Content' }),
  cache: createLogger({ prefix: 'Cache' }),
  media: createLogger({ prefix: 'Media' }),
  ai: createLogger({ prefix: 'AI' }),
  voting: createLogger({ prefix: 'Voting' }),
};

// Export factory for custom loggers
export { createLogger };

// Type exports
export type { LogLevel, LogContext, LoggerConfig };

